/**
 * engineNormalJson_Helper.ts - Helper methods for EngineNormalJson
 * Pure ES Module version
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { ParseHtmlJson } from '../../base/loader/parseHtmlJson.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { JsonArray, JsonObject, JsonValue, JsonValueType } from '../../../../Assembler/dist/app/json/index.js';
import type { EngineNormalJson } from './engineNormalJson.js';
import type { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';
import type { InheritanceOperation } from '../../../../Assembler/dist/model/inheritanceOperation.js';
import type { OperationsMetadata } from '../../../../Assembler/dist/model/operationsMetadata.js';
import { CommonUtil } from '../../../../Assembler/dist/common/commonUtil.js';

type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

/**
 * Returns keys as array preserving insertion order
 * Keys have numeric prefix (001_, 002_) for ordering
 */
export function getKeysList(keys: string[]): string[] {
    return keys.slice(); // Preserve insertion order
}

/**
 * Converts a value to string representation
 */
export function stringifyValue(value: unknown): string {
    if (value === null || value === undefined)
        return '';

    // Handle JsonValue wrappers - unwrap to get actual value
    if (value instanceof JsonValue) {
        if (value.typeName === JsonValueType.String) {
            return value.asString() ?? '';
        } else if (value.typeName === JsonValueType.Bool) {
            return value.asBool() ? 'true' : 'false';
        } else if (value.typeName === JsonValueType.Integer) {
            return (value.asInteger() ?? 0).toString();
        } else if (value.typeName === JsonValueType.Number) {
            return (value.asNumber() ?? 0).toString();
        } else if (value.typeName === JsonValueType.Null) {
            return 'null';
        }
        // For Array and Object types, fall through to toString()
    }

    if (typeof value === 'string')
        return value;

    if (typeof value === 'boolean')
        return value ? 'true' : 'false';

    return (value as { toString(): string }).toString();
}

/**
 * Converts a value to boolean
 */
export function convertToBoolean(value: unknown): boolean {
    if (value === null || value === undefined)
        return false;

    // Handle JsonValue wrappers
    if (value instanceof JsonValue) {
        if (value.typeName === JsonValueType.Bool || value.typeName === JsonValueType.Integer || value.typeName === JsonValueType.Number)
            return value.toBool();
        // Array truthiness: non-empty = true
        if (value.typeName === JsonValueType.Array) {
            const arr = value.asArray();
            return arr !== null && arr.length > 0;
        }
        // Object: exists = true
        if (value.typeName === JsonValueType.Object)
            return true;
        // String: non-empty and not "false"/"0"
        if (value.typeName === JsonValueType.String) {
            const s = value.asString() ?? '';
            return s.length > 0 &&
                   s.toLowerCase() !== 'false' &&
                   s.toLowerCase() !== '0';
        }
        Logger.fallback("F-COE3",
            `ConvertToBoolean: present JsonValue of unrecognized shape (IsNull=${value.isNull})`);
        throw new Error(`Cannot convert JsonValue of type ${value.typeName} to boolean.`);
    }

    if (typeof value === 'boolean')
        return value;

    if (typeof value === 'string') {
        // Non-empty and not "false"/"0"
        return value.length > 0 &&
               value.toLowerCase() !== 'false' &&
               value.toLowerCase() !== '0';
    }

    if (typeof value === 'number')
        return value !== 0;

    // Array truthiness: non-empty = true
    if (Array.isArray(value) || value instanceof JsonArray)
        return (value as unknown[] | JsonArray).length > 0;

    // Object: exists = true
    if (typeof value === 'object')
        return true;

    return false;
}


/**
 * Unwraps a JsonValue to get the raw value
 */
function unwrapJsonValue(jsonValue: JsonValue): string | number | boolean | unknown[] | PlainJsonObject | null {
    switch (jsonValue.typeName) {
        case JsonValueType.String:
            return jsonValue.asString();
        case JsonValueType.Number:
            return jsonValue.asNumber();
        case JsonValueType.Integer:
            return jsonValue.asInteger();
        case JsonValueType.Bool:
            return jsonValue.asBool();
        case JsonValueType.Array:
            return jsonValue.asArray() as unknown[];
        case JsonValueType.Object:
            return jsonValue.asObject() as unknown as PlainJsonObject;
        case JsonValueType.Null:
            return null;
        default:
            return null;
    }
}

/**
 * Gets a value from a JsonObject (Map-based)
 * If the value is a JsonValue, returns the raw value
 */
function getValue(obj: JsonObject | null, key: string): unknown {
    let value: unknown;
    if (obj instanceof Map) {
        value = obj.get(key);
    } else {
        value = obj ? (obj as PlainJsonObject)[key] : undefined;
    }

    // Unwrap JsonValue to get raw value
    if (value instanceof JsonValue) {
        return unwrapJsonValue(value);
    }
    return value;
}

/**
 * Gets all keys from a JsonObject (Map-based)
 */
function getKeys(obj: JsonObject | null): string[] {
    if (obj instanceof Map) {
        return Array.from(obj.keys());
    }
    return obj ? Object.keys(obj) : [];
}

/**
 * Evaluates a conditional key from item data
 */
export function evaluateCondition(conditionalKey: string, itemData: JsonObject): boolean {
    // Case-insensitive key lookup
    for (const key of getKeys(itemData)) {
        if (key.toLowerCase() === conditionalKey.toLowerCase()) {
            return convertToBoolean(getValue(itemData, key));
        }
    }

    return false; // Key not found - default to false
}

/**
 * Evaluates a conditional key with CONTEXT PRIORITY (mirrors csharp EngineNormalJson_Merge_Conditional /
 * Normal's BuildMergedJson Step 3: contextJson always wins). Without this, a slot like
 * "IsAuthenticated":"{{IsAuthenticated}}" is evaluated as the unresolved placeholder string (truthy)
 * instead of the real context bool, so the JSON engines render {{?IsAuthenticated}}/{{^IsAuthenticated}}
 * blocks differently than the Normal/PreProcess engines.
 */
export function evaluateConditionWithContext(conditionalKey: string, contextJson: JsonObject | null, itemData: JsonObject): boolean {
    if (contextJson !== null) {
        for (const key of getKeys(contextJson)) {
            if (key.toLowerCase() === conditionalKey.toLowerCase()) {
                return convertToBoolean(getValue(contextJson, key));
            }
        }
    }
    return evaluateCondition(conditionalKey, itemData);
}

/**
 * Parses a JSON array string into a JsonArray of JsonObjects
 */
export function parseJsonArray(jsonArrayString: string): JsonArray {
    const result = new JsonArray();

    // Trim whitespace
    jsonArrayString = jsonArrayString.trim();

    if (!jsonArrayString.startsWith('[') || !jsonArrayString.endsWith(']'))
        return result;

    // Remove opening and closing brackets
    const content = jsonArrayString.substring(1, jsonArrayString.length - 1).trim();
    if (!content)
        return result;

    // Split by object boundaries
    const objects: string[] = [];
    let braceDepth = 0;
    let startIndex = 0;

    for (let i = 0; i < content.length; i++) {
        if (content[i] === '{') {
            if (braceDepth === 0)
                startIndex = i;
            braceDepth++;
        } else if (content[i] === '}') {
            braceDepth--;
            if (braceDepth === 0) {
                const obj = content.substring(startIndex, i + 1);
                objects.push(obj);
            }
        }
    }

    // Parse each object using ParseHtmlJson utility
    for (const obj of objects) {
        try {
            const dict = ParseHtmlJson.parse(obj);
            // Convert to JsonObject if not already
            if (dict instanceof JsonObject) {
                result.push(dict);
            } else {
                const jsonObj = new JsonObject();
                for (const [key, value] of Object.entries(dict as PlainJsonObject)) {
                    jsonObj.set(key, value as string);
                }
                result.push(jsonObj);
            }
        } catch {
            // Skip malformed objects
        }
    }

    return result;
}

export function createFlowStack(appFile: string): string[] {
    return [appFile];
}

export function buildOperationsMetadata(
    genOperationJson: boolean,
    appSite: string,
    appFile: string,
    appView: string | null,
    templateKey: string,
    operations: TemplateOperation[],
    inheritanceOperations: InheritanceOperation[]
): OperationsMetadata | null {
    if (!genOperationJson) {
        return null;
    }

    return {
        appSite,
        appFile,
        appView: appView || '',
        templateName: templateKey,
        operations,
        hasInheritance: inheritanceOperations.length > 0,
        inheritanceOperations
    } as OperationsMetadata;
}

export function accumulateDynamicDataTimingFromLoader(engine: EngineNormalJson): void {
    const { nanos, count } = engine._loader!.getDynamicDataTiming();
    engine.accumulateDynamicDataTiming(nanos, count);
}

export function accumulateDynamicViewTimingFromLoader(engine: EngineNormalJson): void {
    const { nanos, count } = engine._loader!.getDynamicViewTiming();
    engine.accumulateDynamicViewTiming(nanos, count);
}

export function accumulateDynamicActionTimingFromLoader(engine: EngineNormalJson): void {
    const { nanos, count } = engine._loader!.getDynamicActionTiming();
    engine.accumulateDynamicActionTiming(nanos, count);
}

export function accumulateDynamicClientDataTimingFromLoader(engine: EngineNormalJson): void {
    const { nanos, count } = engine._loader!.getDynamicClientDataTiming();
    engine.accumulateDynamicClientDataTiming(nanos, count);
}

export function convertDynamicViewResults(dynamicViewResults: JsonObject): Record<string, string> {
    const dynamicView: Record<string, string> = {};

    for (const [key, value] of dynamicViewResults.entries()) {
        if (value.isObject) {
            const html = GenerateHtmlJson.convertJsonToHtml(value.getObject());
            dynamicView[key] = CommonUtil.normalizeFileContent(html);
        } else if (value.isString) {
            dynamicView[key] = value.getString();
        }
    }

    return dynamicView;
}

export const helperMethods = {
    wrapJsonArrayWithControlElements(this: EngineNormalJson, jsonArray: JsonArray | unknown[]): JsonArray {
        const wrappedItems = new JsonArray();
        for (let item of jsonArray as unknown[]) {
            if (item instanceof JsonValue) {
                if (item.isObject) {
                    wrappedItems.push(item.asObject());
                    continue;
                }

                const simpleObj = new JsonObject();
                simpleObj.set('Value', item);
                wrappedItems.push(simpleObj);
                continue;
            }

            if (typeof item === 'object' && item !== null && !Array.isArray(item) && !(item instanceof JsonArray)) {
                if (item instanceof JsonObject) {
                    wrappedItems.push(item);
                } else {
                    const obj = new JsonObject();
                    const itemEntries = item instanceof Map ? (item as PlainJsonMap).entries() : Object.entries(item as PlainJsonObject);
                    for (const [subKey, subValue] of itemEntries) {
                        obj.set(subKey, subValue as string);
                    }
                    wrappedItems.push(obj);
                }
            } else {
                const simpleObj = new JsonObject();
                simpleObj.set('Value', item as string);
                wrappedItems.push(simpleObj);
            }
        }

        return wrappedItems;
    }
};
